/**
 * 開発ポイント推移カード（推移 / 日進 の 2 ビュー切替）
 *
 * - 「推移」: getMemberTrend の月内累積（MTD, 月初リセット）開発pt を折れ線で表示し、
 *   個人の目標線（期待値戦闘力 × 累積稼働日数 = 月末で当月総目標pt に到達）を重ねる。
 * - 「日進」: 日次の完了pt を棒グラフで描き、その日が属する月の期待値戦闘力
 *   （= 稼働日あたりの期待pt）を目標線（月ごとの水平線）として重ねる。さらに、
 *   現時点戦闘力（累積完了pt ÷ 累積稼働日数、当日は処理中pt 込み＝サマリーと同口径）を
 *   ピンクの折れ線で表示する。目標を下回った日を一目で分かるよう棒を色分けする。
 *   本人が「目標設定」で自己設定目標線を登録している場合は、1 本ごとに指定期間だけ
 *   指定色の破線として追加で重ねる（期待値戦闘力の目標線・棒の色分け基準は変えない）。
 *   期間の重ならない目標線は何本でも同時に描ける（例: 8月 4pt / 9月 6pt）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business メンバーの開発pt 推移カード。推移(月内累積折れ線+個人目標線)と日進(日次完了pt 棒 + 期待値戦闘力の水平目標線 + 現時点戦闘力ピンク折れ線 + 本人が設定した自己設定目標線を期間ごとに複数)を切替表示し、目標未達の日を色分けで可視化する
 * @graph-connects DevelopPointsTargetModal [calls] 日進ビューの「目標設定」モーダルを開くボタンを提供する
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { extractDateString } from '../../lib/date-utils.js';
import { formatPt } from '../../lib/format.js';
import { formatNum } from './combat-power/combat-power-utils.js';
import type { MemberTrendPoint } from '../../types/api.js';
import type { DevelopPointTarget } from '../../types/develop-point-target.js';

/** 開発ポイント推移カードの表示ビュー @graph-connects none */
export type DevelopPointsView = 'trend' | 'daily';

/** 目標線の色（戦闘力チャートの「期待値」と同じインディゴ） @graph-connects none */
const TARGET_COLOR = '#6366f1';
/** 現時点戦闘力ラインの色（活発なピンク） @graph-connects none */
const POWER_COLOR = '#ec4899';

/**
 * チャート描画領域の高さ。ローディング / 空状態も同じ高さにしてカードの伸縮を防ぐ。
 * 凡例を独立行にした分（+ 自己設定目標の 1 項目）カード全体が縦に伸びるため、
 * チャート側も合わせて高くして「チャート : 凡例」の比率が崩れないようにする。
 * 隣に並ぶリリース数推移カードも同じ高さに揃えている。
 * @graph-connects none
 */
export const DEVELOP_POINTS_CHART_HEIGHT = 'h-60';

/**
 * 日進ビューのチャート 1 行。自己設定目標線は本数が可変なので
 * customTarget0, customTarget1, ... を動的なキーとして持たせる。
 * @graph-connects none
 */
interface DailyChartRow {
  date: string;
  value: number;
  target: number | null;
  combatPower: number | null;
  [customTargetSeriesKey: string]: string | number | null;
}

/** @graph-connects none */
interface DevelopPointsTrendCardProps {
  /** getMemberTrend の日次データ（date 昇順・developPoints は月内累積・cumulativeBusinessDays 付き） */
  data: MemberTrendPoint[] | undefined;
  /** トレンド取得中か */
  isLoading: boolean;
  /** 月("YYYY/MM") → 期待値戦闘力（= 稼働日あたり期待pt）。日進の水平目標線・推移の目標ランプに使う */
  expectedByMonth: Map<string, number>;
  /** 当月の処理中pt 合計（サマリーの wipPt と同口径）。日進の現時点戦闘力ラインの当日分子に加算する */
  wipPt: number | null;
  /** 日付クリック時のドリルダウン。mode='day' は単日、'range' は期間（推移ビュー） */
  onSelectDate: (date: string, mode: 'range' | 'day') => void;
  /** 本人が設定した自己設定目標線の一覧。日進ビューで 1 本ずつ指定期間だけ破線を重ねる */
  customTargets?: DevelopPointTarget[];
  /** 「目標設定」ボタンを出すか（= 閲覧者がこのメンバー本人か）。既定は出さない */
  canEditTarget?: boolean;
  /** 「目標設定」ボタン押下時。canEditTarget が true のときのみ使う */
  onEditTarget?: () => void;
}

/** ISO 日付("YYYY-MM-DD") → 戦闘力履歴と同形式の月キー "YYYY/MM" @graph-connects none */
function monthKeyOf(dateIso: string): string {
  return `${dateIso.slice(0, 4)}/${dateIso.slice(5, 7)}`;
}

/** 小数第1位丸め @graph-connects none */
function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

/**
 * 月内累積（MTD, 月初リセット）の開発pt 系列を日次差分へ変換する。
 * getMemberTrend は月ごとに積み上げ直す累積値を返すため、同一月内は前日との差分、
 * 月替わりの初日（その月で範囲内最初の行）はその日の累積値がそのまま日次値になる。
 * 累積は月内単調増加のため差分は非負。浮動小数の誤差のみ丸め（小数1位）で吸収する。
 * @graph-connects none
 */
export function toDailyDevelopPoints(
  points: MemberTrendPoint[],
): { date: string; developPoints: number }[] {
  let prevMonth: string | null = null;
  let prevCumulative = 0;
  return points.map((p) => {
    const monthKey = p.date.slice(0, 7); // YYYY-MM
    const cumulative = p.developPoints;
    const daily = monthKey === prevMonth ? cumulative - prevCumulative : cumulative;
    prevMonth = monthKey;
    prevCumulative = cumulative;
    return { date: p.date, developPoints: Math.max(0, round1(daily)) };
  });
}

/**
 * 現時点戦闘力（= 累積完了pt ÷ 累積稼働日数）を各日について求める。
 * 末尾（当日 / ライブ日）は処理中pt を分子に加える（サマリーの処理中pt 込み戦闘力と同口径）。
 * 稼働日数が無い / 0 の日は判定不能として null（フロントは線を繋がない）。
 * @graph-connects none
 */
export function toCurrentPower(
  points: MemberTrendPoint[],
  wipPt: number | null,
): (number | null)[] {
  return points.map((p, i) => {
    const bd = p.cumulativeBusinessDays;
    if (bd === null || bd <= 0) return null;
    const isLive = i === points.length - 1;
    const numerator = p.developPoints + (isLive ? (wipPt ?? 0) : 0);
    return round1(numerator / bd);
  });
}

/**
 * 日次完了pt と その月の期待値戦闘力（目標）から棒の色を決める。
 * - 0pt（非稼働日 / 未着手）: 判定せず muted（稼働日か否かをクライアントで持たないため未達扱いしない）
 * - 目標不明（期待値 null / 0 以下）: info
 * - 目標達成（>=）: success / 未達: warning
 * @graph-connects none
 */
export function dailyBarColor(value: number, target: number | null): string {
  if (value <= 0) return 'var(--color-border)';
  if (target === null || target <= 0) return 'var(--color-info)';
  return value >= target ? 'var(--color-success)' : 'var(--color-warning)';
}

/**
 * 日次完了pt の棒 1 本を、目標達成/未達で色分けして描画する Bar の shape。
 * recharts の Cell が deprecated のため shape レンダラーで棒ごとに fill を切替える。
 * @graph-connects none
 */
function DailyBarShape(props: {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  payload?: { value?: number; target?: number | null };
}) {
  const { x, y, width, height, payload } = props;
  if (x == null || y == null || width == null || height == null) return null;
  const fill = dailyBarColor(payload?.value ?? 0, payload?.target ?? null);
  return <rect x={x} y={y} width={width} height={height} fill={fill} />;
}

/**
 * 自己設定目標線の、その日の値を返す。適用期間外は null（線を引かない）。
 * 日付は API が YYYY-MM-DD へ正規化済みなので辞書順比較で期間判定できる。
 * @graph-connects none
 */
export function customTargetValueAt(
  dateIso: string,
  customTarget: DevelopPointTarget | null | undefined,
): number | null {
  if (!customTarget) return null;
  return dateIso >= customTarget.startDate && dateIso <= customTarget.endDate
    ? customTarget.targetPt
    : null;
}

/**
 * 自己設定目標線 n 本目の recharts dataKey。
 * 目標線ごとに色が違うため 1 本 1 系列にする必要がある（1 系列では色を塗り分けられない）。
 * @graph-connects none
 */
export function customTargetSeriesKey(index: number): string {
  return `customTarget${String(index)}`;
}

/**
 * 表示期間（チャートに出ている日付）に 1 日でも掛かる目標線だけを残す。
 * 掛からない目標線は線も凡例も出さない（描いても見えず凡例だけ増えるため）。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business チャートの表示期間に掛かる自己設定目標線だけを抽出する
 * @graph-connects none
 */
export function visibleCustomTargets(
  dates: string[],
  customTargets: DevelopPointTarget[],
): DevelopPointTarget[] {
  if (dates.length === 0) return [];
  const first = dates[0];
  const last = dates[dates.length - 1];
  return customTargets.filter((t) => t.startDate <= last && first <= t.endDate);
}

/** 凡例・ツールチップ用の期間ラベル（M/D〜M/D） @graph-connects none */
export function customTargetPeriodLabel(target: DevelopPointTarget): string {
  const md = (iso: string) =>
    `${String(Number(iso.slice(5, 7)))}/${String(Number(iso.slice(8, 10)))}`;
  return `${md(target.startDate)}〜${md(target.endDate)}`;
}

/** X 軸の日付ラベル（M/D）。パースできなければ元文字列 @graph-connects none */
function formatAxisDate(value: string): string {
  const d = new Date(extractDateString(value));
  return isNaN(d.getTime())
    ? extractDateString(value)
    : `${String(d.getMonth() + 1)}/${String(d.getDate())}`;
}

/** ツールチップの日付ラベル（ロケール表記）。パースできなければ元文字列 @graph-connects none */
function formatTooltipDate(label: string): string {
  const d = new Date(extractDateString(label));
  return isNaN(d.getTime()) ? extractDateString(label) : d.toLocaleDateString();
}

/** ツールチップ共通スタイル @graph-connects none */
const TOOLTIP_CONTENT_STYLE = {
  backgroundColor: 'var(--color-bg-elevated)',
  border: '1px solid var(--color-border)',
  borderRadius: '8px',
  boxShadow: 'var(--shadow-lg)',
};

/**
 * 推移 / 日進 を切替えるピル型セグメントスイッチ（チャート隅の極小トグル）。
 * @graph-connects none
 */
function DevelopPointsViewSwitch({
  view,
  onChange,
}: {
  view: DevelopPointsView;
  onChange: (next: DevelopPointsView) => void;
}) {
  const { t } = useTranslation();
  const segClass = (active: boolean) =>
    `px-2.5 py-0.5 rounded-full text-[11px] font-semibold transition-colors leading-none ${
      active
        ? 'bg-[var(--color-bg-surface)] text-[var(--color-text-primary)] shadow-sm'
        : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
    }`;
  return (
    <div
      className="inline-flex items-center gap-0.5 rounded-full p-0.5 border border-[var(--color-border)] bg-[var(--color-bg-elevated)]"
      role="group"
      aria-label={t('members.detail.developPointsViewSwitchLabel')}
    >
      <button
        type="button"
        onClick={() => {
          onChange('trend');
        }}
        className={segClass(view === 'trend')}
        data-testid="develop-points-view-trend"
      >
        {t('members.detail.developPointsViewTrend')}
      </button>
      <button
        type="button"
        onClick={() => {
          onChange('daily');
        }}
        className={segClass(view === 'daily')}
        data-testid="develop-points-view-daily"
      >
        {t('members.detail.developPointsViewDaily')}
      </button>
    </div>
  );
}

/** 凡例の 1 項目（色スウォッチ + ラベル） @graph-connects none */
function LegendItem({ swatch, label }: { swatch: React.ReactNode; label: string }) {
  return (
    <span className="flex items-center gap-1">
      {swatch}
      {label}
    </span>
  );
}

/** 日進ビューの凡例（達成 / 未達 / 目標線 / 現時点戦闘力 / 自己設定目標線 n 本） @graph-connects none */
function DailyLegend({ customTargets }: { customTargets: DevelopPointTarget[] }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center flex-wrap gap-x-3 gap-y-1 text-[11px] text-[var(--color-text-muted)]">
      <LegendItem
        swatch={<span className="inline-block w-2.5 h-2.5 rounded-sm bg-[var(--color-success)]" />}
        label={t('members.detail.developPointsDailyMet')}
      />
      <LegendItem
        swatch={<span className="inline-block w-2.5 h-2.5 rounded-sm bg-[var(--color-warning)]" />}
        label={t('members.detail.developPointsDailyMissed')}
      />
      <LegendItem
        swatch={<span className="inline-block w-4 border-t-2 border-dashed border-[#6366f1]" />}
        label={t('members.detail.developPointsDailyTarget')}
      />
      <LegendItem
        swatch={<span className="inline-block w-4 border-t-2 border-[#ec4899]" />}
        label={t('members.detail.developPointsCurrentPower')}
      />
      {/* 目標線が複数あるときは、どの破線がどの期間かを凡例で見分けられるようにする */}
      {customTargets.map((target) => (
        <LegendItem
          key={target.id}
          swatch={
            <span
              className="inline-block w-4 border-t-2 border-dashed"
              style={{ borderColor: target.color }}
            />
          }
          label={
            customTargets.length > 1
              ? `${t('members.detail.developPointsCustomTarget')} ${customTargetPeriodLabel(target)}`
              : t('members.detail.developPointsCustomTarget')
          }
        />
      ))}
    </div>
  );
}

/** 推移ビューの凡例（累積完了pt / 個人目標線） @graph-connects none */
function TrendLegend() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center flex-wrap gap-x-3 gap-y-1 text-[11px] text-[var(--color-text-muted)]">
      <LegendItem
        swatch={<span className="inline-block w-4 border-t-2 border-[var(--color-success)]" />}
        label={t('members.detail.developPointsCumulative')}
      />
      <LegendItem
        swatch={<span className="inline-block w-4 border-t-2 border-dashed border-[#6366f1]" />}
        label={t('members.detail.developPointsTrendTarget')}
      />
    </div>
  );
}

/** @graph-connects none */
export function DevelopPointsTrendCard({
  data,
  isLoading,
  expectedByMonth,
  wipPt,
  onSelectDate,
  customTargets = [],
  canEditTarget = false,
  onEditTarget,
}: DevelopPointsTrendCardProps) {
  const { t } = useTranslation();
  const [view, setView] = useState<DevelopPointsView>('trend');

  const points = data ?? [];
  const hasData = points.length > 0;

  // 推移ビュー用: 月内累積 完了pt（折れ線）+ 個人目標ランプ（期待値戦闘力 × 累積稼働日数）
  const trendChartData = points.map((p) => {
    const expected = expectedByMonth.get(monthKeyOf(p.date)) ?? null;
    const bd = p.cumulativeBusinessDays;
    return {
      date: p.date,
      value: p.developPoints,
      target: expected !== null && bd !== null && bd > 0 ? round1(expected * bd) : null,
    };
  });

  // 日進ビュー用: 日次完了pt（棒）+ 月次期待値戦闘力（水平目標線）+ 現時点戦闘力（ピンク折れ線）
  const daily = toDailyDevelopPoints(points);
  const currentPower = toCurrentPower(points, wipPt);
  // 表示期間に 1 日も掛かっていない自己設定目標線は、線も凡例も出さない
  const visibleTargets = visibleCustomTargets(
    points.map((p) => p.date),
    customTargets,
  );

  const dailyChartData: DailyChartRow[] = points.map((p, i) => {
    const row: DailyChartRow = {
      date: p.date,
      value: daily[i].developPoints,
      target: expectedByMonth.get(monthKeyOf(p.date)) ?? null,
      combatPower: currentPower[i],
    };
    // 自己設定目標線は 1 本 1 系列。指定期間の外は null にして線を途切れさせる
    // （棒の達成/未達の色分け基準には使わない）。
    visibleTargets.forEach((target, index) => {
      row[customTargetSeriesKey(index)] = customTargetValueAt(p.date, target);
    });
    return row;
  });

  return (
    <Card className="animate-content-reveal">
      <CardHeader
        // items-end で、右上のボタン下端をタイトル下の説明文の下端に揃える
        className="items-end"
        title={t('members.detail.developPointsTrend')}
        description={
          view === 'daily'
            ? t('members.detail.developPointsDailyDescription')
            : t('members.detail.developPointsTrendDescription')
        }
        action={
          view === 'daily' && canEditTarget && onEditTarget ? (
            <Button
              variant="primary"
              size="sm"
              // my-page の「＋案件を起票する」と同じピル型（primary / sm / rounded-full）
              className="shrink-0 whitespace-nowrap rounded-full"
              onClick={onEditTarget}
              data-testid="develop-points-target-open"
            >
              {t('members.detail.developPointsTargetSetting')}
            </Button>
          ) : undefined
        }
      />
      <CardContent>
        {isLoading ? (
          <div className={`${DEVELOP_POINTS_CHART_HEIGHT} flex items-center justify-center`}>
            <div className="loading-spinner" />
          </div>
        ) : hasData ? (
          <>
            <div className={DEVELOP_POINTS_CHART_HEIGHT}>
              <ResponsiveContainer width="100%" height="100%">
                {view === 'trend' ? (
                  <ComposedChart
                    data={trendChartData}
                    margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                    onClick={
                      ((state: { activeLabel?: string | number }) => {
                        if (state.activeLabel != null) {
                          onSelectDate(extractDateString(state.activeLabel), 'range');
                        }
                      }) as never
                    }
                    style={{ cursor: 'pointer' }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis
                      dataKey="date"
                      stroke="var(--color-text-muted)"
                      fontSize={11}
                      tickFormatter={formatAxisDate}
                    />
                    <YAxis
                      stroke="var(--color-text-muted)"
                      fontSize={11}
                      tickFormatter={(v: number) => String(v)}
                    />
                    <Tooltip
                      wrapperStyle={{ zIndex: 50, outline: 'none' }}
                      contentStyle={TOOLTIP_CONTENT_STYLE}
                      labelStyle={{ color: 'var(--color-text-primary)' }}
                      formatter={
                        ((value: number, _name: string, item: { dataKey?: string }) =>
                          item.dataKey === 'target'
                            ? [`${formatPt(value)}pt`, t('members.detail.developPointsTrendTarget')]
                            : [
                                `${formatPt(value)}pt`,
                                t('members.detail.developPointsCumulative'),
                              ]) as never
                      }
                      labelFormatter={((label: string) => formatTooltipDate(label)) as never}
                    />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="var(--color-success)"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4, fill: 'var(--color-success)' }}
                    />
                    <Line
                      type="monotone"
                      dataKey="target"
                      stroke={TARGET_COLOR}
                      strokeWidth={2}
                      strokeDasharray="4 4"
                      dot={false}
                      connectNulls
                    />
                  </ComposedChart>
                ) : (
                  <ComposedChart
                    data={dailyChartData}
                    margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                    onClick={
                      ((state: { activeLabel?: string | number }) => {
                        if (state.activeLabel != null) {
                          onSelectDate(extractDateString(state.activeLabel), 'day');
                        }
                      }) as never
                    }
                    style={{ cursor: 'pointer' }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis
                      dataKey="date"
                      stroke="var(--color-text-muted)"
                      fontSize={11}
                      tickFormatter={formatAxisDate}
                    />
                    <YAxis
                      stroke="var(--color-text-muted)"
                      fontSize={11}
                      tickFormatter={(v: number) => String(v)}
                    />
                    <Tooltip
                      wrapperStyle={{ zIndex: 50, outline: 'none' }}
                      contentStyle={TOOLTIP_CONTENT_STYLE}
                      labelStyle={{ color: 'var(--color-text-primary)' }}
                      formatter={
                        ((value: number, _name: string, item: { dataKey?: string }) => {
                          if (item.dataKey === 'target') {
                            return [
                              formatNum(value, 1),
                              t('members.detail.developPointsDailyTarget'),
                            ];
                          }
                          if (item.dataKey === 'combatPower') {
                            return [
                              formatNum(value, 1),
                              t('members.detail.developPointsCurrentPower'),
                            ];
                          }
                          if (item.dataKey?.startsWith('customTarget') === true) {
                            return [
                              formatNum(value, 1),
                              t('members.detail.developPointsCustomTarget'),
                            ];
                          }
                          return [
                            `${formatPt(value)}pt`,
                            t('members.detail.developPointsDailyActual'),
                          ];
                        }) as never
                      }
                      labelFormatter={((label: string) => formatTooltipDate(label)) as never}
                    />
                    <Bar dataKey="value" shape={DailyBarShape as never} />
                    <Line
                      type="stepAfter"
                      dataKey="target"
                      stroke={TARGET_COLOR}
                      strokeWidth={2}
                      strokeDasharray="4 4"
                      dot={false}
                      connectNulls
                    />
                    <Line
                      type="monotone"
                      dataKey="combatPower"
                      stroke={POWER_COLOR}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4, fill: POWER_COLOR }}
                      connectNulls
                    />
                    {visibleTargets.map((target, index) => (
                      <Line
                        key={target.id}
                        type="linear"
                        dataKey={customTargetSeriesKey(index)}
                        stroke={target.color}
                        strokeWidth={2}
                        strokeDasharray="4 4"
                        dot={false}
                      />
                    ))}
                  </ComposedChart>
                )}
              </ResponsiveContainer>
            </div>
            {/* 凡例はチャート直下の独立行に中央寄せで置く（ビュー切替と同じ行に混ぜると
                項目数が増えたとき折り返しで左右がちぐはぐに見えるため）。切替は従来どおり右下。 */}
            <div className="mt-3 flex justify-center">
              {view === 'daily' ? <DailyLegend customTargets={visibleTargets} /> : <TrendLegend />}
            </div>
            <div className="mt-2 flex justify-end">
              <DevelopPointsViewSwitch view={view} onChange={setView} />
            </div>
          </>
        ) : (
          <div
            className={`${DEVELOP_POINTS_CHART_HEIGHT} flex items-center justify-center border border-dashed border-[var(--color-border)] rounded-lg`}
          >
            <p className="text-[var(--color-text-muted)]">{t('status.noData')}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
